import React, { createContext, useContext, useEffect, useState } from "react";
import { ConfigProvider, Segmented, Tooltip } from "antd";
import { antdTheme } from "./antdTheme";

// App-wide A / A+ / A++ text-size preference. Some users find the app's
// typography too small, so the main header carries a switcher whose choice is
// stored in localStorage and re-applied on every visit.
//
// Mechanism (text-only scaling — layout, spacing and sidebar widths stay put):
//   1. An inline font-size on <html> overrides the SCSS 16px base, scaling
//      every rem/em-derived size in the DashLite stylesheets.
//   2. The antd fontSize token (via the app-root ConfigProvider rendered by
//      AppFontScaleProvider) scales all antd component text.
//   3. html[data-font-scale] drives assets/scss/_font-scale.scss, which
//      carries the px-anchored DashLite controls (buttons, fields, dropdowns,
//      nav links) along at the same ratio.
//   4. useFontScale().fs(px) scales hardcoded inline px sizes in components
//      that opt in (the report pages).
// echarts canvases keep their own px sizing and intentionally do not scale.

export type FontScaleKey = "normal" | "large" | "xlarge";

const STORAGE_KEY = "pxAppFontScale";
// The toggle started life on the 3M Consolidation report under this key —
// honor an existing choice so early adopters keep their preference.
const LEGACY_STORAGE_KEY = "pmsReportFontScale";

export const FONT_SCALE_OPTIONS: Array<{
  key: FontScaleKey;
  label: string;
  scale: number;
}> = [
  { key: "normal", label: "A", scale: 1 },
  { key: "large", label: "A+", scale: 1.15 },
  { key: "xlarge", label: "A++", scale: 1.3 },
];

const isFontScaleKey = (value: string | null): value is FontScaleKey =>
  FONT_SCALE_OPTIONS.some((option) => option.key === value);

export const readStoredFontScale = (): FontScaleKey => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (isFontScaleKey(stored)) return stored;
  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
  return isFontScaleKey(legacy) ? legacy : "normal";
};

export const storeFontScale = (key: FontScaleKey) => {
  localStorage.setItem(STORAGE_KEY, key);
};

export const getFontScale = (key: FontScaleKey): number =>
  FONT_SCALE_OPTIONS.find((option) => option.key === key)?.scale ?? 1;

// antd's base fontSize token is 14 — keep the token an integer.
export const antdFontSizeToken = (scale: number) => Math.round(14 * scale);

const FontScaleContext = createContext(1);

export const useFontScale = () => {
  const scale = useContext(FontScaleContext);
  const fs = (px: number) => Math.round(px * scale * 10) / 10;
  return { scale, fs };
};

const FontScaleSettingContext = createContext<{
  fontScaleKey: FontScaleKey;
  setFontScaleKey: (key: FontScaleKey) => void;
}>({ fontScaleKey: "normal", setFontScaleKey: () => {} });

export const useFontScaleSetting = () => useContext(FontScaleSettingContext);

// App-root provider: owns the preference, applies the <html> overrides and
// wraps the tree in the themed ConfigProvider (replaces the plain
// <ConfigProvider theme={antdTheme}> in index.tsx).
export const AppFontScaleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [fontScaleKey, setKey] = useState<FontScaleKey>(readStoredFontScale);
  const scale = getFontScale(fontScaleKey);

  const setFontScaleKey = (key: FontScaleKey) => {
    setKey(key);
    storeFontScale(key);
  };

  useEffect(() => {
    const root = document.documentElement;
    if (scale === 1) {
      root.style.removeProperty("font-size");
      delete root.dataset.fontScale;
    } else {
      root.style.fontSize = `${16 * scale}px`;
      root.dataset.fontScale = fontScaleKey;
    }
  }, [fontScaleKey, scale]);

  return (
    <FontScaleSettingContext.Provider value={{ fontScaleKey, setFontScaleKey }}>
      <FontScaleContext.Provider value={scale}>
        <ConfigProvider
          theme={{
            ...antdTheme,
            token: { ...antdTheme.token, fontSize: antdFontSizeToken(scale) },
          }}
        >
          {children}
        </ConfigProvider>
      </FontScaleContext.Provider>
    </FontScaleSettingContext.Provider>
  );
};

// The switcher keeps fixed label sizes (12 / 13.5 / 15) as a preview of the
// three steps — it must not grow with the selection.
export const FontSizeSwitcher: React.FC = () => {
  const { fontScaleKey, setFontScaleKey } = useFontScaleSetting();
  return (
    <Tooltip title="Text size">
      <Segmented
        size="small"
        value={fontScaleKey}
        onChange={(next) => setFontScaleKey(next as FontScaleKey)}
        options={FONT_SCALE_OPTIONS.map((option, index) => ({
          value: option.key,
          label: (
            <span style={{ fontSize: 12 + index * 1.5, fontWeight: 600 }}>
              {option.label}
            </span>
          ),
        }))}
      />
    </Tooltip>
  );
};
